import classNames from "classnames";
import { Fragment, useRef, useState } from "react";
import { createPortal } from "react-dom";
import useOnClickOutside from "../../hooks/useOnclickOutside";
import { Glyph } from "../../types";
import SvgMoreVert from "../icons/MoreVert";
import Icon from "./Icon";
import styles from "./ListItemMenu.module.css";

type XOR<T, U> = (T & { [K in keyof U]?: never }) | (U & { [K in keyof T]?: never });

export type Action<T> = {
  onClick: (item: T) => void;
  isDisabled?: (item: T) => boolean;
  isDivider?: boolean;
  icon?: Glyph;
  color?: "red" | "blue" | "green";
} & XOR<{ title: string }, { getTitle: (item: T) => string }>;

interface ListItemMenuProps<T> {
  actions: Action<T>[];
  item: T;
}

const portalRoot = document.getElementById("menu-root")!;

const ListItemMenu = <T,>({ actions, item }: ListItemMenuProps<T>) => {
  const [isModalOpen, setModalOpen] = useState(false);
  const [menuPosition, setMenuPosition] = useState<{ left: number; top: number } | null>(null);

  const menuRef = useRef<HTMLDivElement>(null);

  useOnClickOutside(menuRef, () => setModalOpen(false));

  const handleIconClick = (e: React.MouseEvent<Element, MouseEvent>) => {
    e.stopPropagation();

    const { pageX, pageY, clientX, clientY } = e;

    setModalOpen(true);

    requestAnimationFrame(() => {
      if (!menuRef.current) return;

      const menuWidth = menuRef.current.offsetWidth;
      const menuHeight = menuRef.current.offsetHeight;

      let left = pageX - menuWidth;
      let top = pageY;

      if (pageX < menuWidth) left = clientX;
      if (window.innerHeight - clientY < menuHeight) {
        top = pageY - menuHeight;
      }

      setMenuPosition({ left, top });
    });
  };

  const menu = isModalOpen && (
    <div
      onClick={(e) => e.stopPropagation()}
      onPointerDown={(e) => e.stopPropagation()}
      ref={menuRef}
      className={styles.menu}
      style={{
        position: "absolute",
        left: menuPosition?.left,
        top: menuPosition?.top,
        visibility: menuPosition ? "visible" : "hidden",
      }}
    >
      <ul className={styles.list}>
        {actions.map((action, index) => {
          const isDisabled = action.isDisabled?.(item);

          return (
            <Fragment key={index}>
              {action.isDivider && <div className={styles.divider} />}

              <li
                className={
                  isDisabled
                    ? styles.disabled
                    : classNames({
                        [styles.green]: action.color === "green",
                        [styles.red]: action.color === "red",
                        [styles.blue]: action.color === "blue",
                      })
                }
                onClick={(e) => {
                  e.preventDefault();
                  if (isDisabled) return;
                  setModalOpen(false);
                  action.onClick(item);
                }}
              >
                {action.icon && <Icon glyph={action.icon} className={styles.icon} />}
                {action.title ?? action.getTitle?.(item)}
              </li>
            </Fragment>
          );
        })}
      </ul>
    </div>
  );

  return (
    <>
      <Icon glyph={SvgMoreVert} onClick={handleIconClick} className={styles.icon} />
      {menu && createPortal(menu, portalRoot)}
    </>
  );
};

export default ListItemMenu;
